import ApiError from "./ApiError";

const config = require("../../env")();

export default abstract class Network {
    public static readonly PROTOCOL = "https://";
    protected static readonly OUTLOOK_REDIRECT = config.OUTLOOK_REDIRECT;
    protected static readonly BASE_URL = `${Network.PROTOCOL}${config.ORPHY_URL}/`;
    protected static readonly API_BASE_URL = `${Network.PROTOCOL}${config.API}/api/v1/`;
    protected static readonly ODATA_BASE_URL = `${Network.PROTOCOL}${config.API}/odata/v3/`;
    protected static readonly IDSRV_URL = `${Network.PROTOCOL}${config.IDSRV}`;
    protected static readonly ADDRESS_PARSER_URL = `${Network.PROTOCOL}${config.ADDRESS_PARSER}`;

    protected static readonly TOKEN_ENDPOINT = "Orphy";

    public abstract authenticate(): Promise<{ token_type: string; access_token: string }>;
    public abstract signout();

    public requestPublicApi = async (urlEnd: string): Promise<Response> => {
        return this.request(`${Network.API_BASE_URL}${urlEnd}`);
    };

    /** Public api counterpart of requestApi, for the callers that want the payload instead of the raw response. */
    public requestJsonPublicApi = async (urlEnd: string): Promise<any> => {
        const url = `${Network.API_BASE_URL}${urlEnd}`;
        const response = await this.request(url);
        return Network.readJsonBody(response, url);
    };

    public requestApi = async (urlEnd: string): Promise<any> => {
        const url = `${Network.ODATA_BASE_URL}${urlEnd}`;
        const response = await this.request(url);
        return Network.readJsonBody(response, url);
    };

    protected request = async (url: string): Promise<Response> => {
        const headers = await this.getHeaders();
        const response = await fetch(url, {
            method: "GET",
            headers: headers,
        });
        Network.ensureOk(response, url);
        return response;
    };

    public sendPublicApi = async (urlEnd: string, data: any = {}): Promise<any> => {
        return this.send(`${Network.API_BASE_URL}${urlEnd}`, data);
    };

    public sendApi = async (urlEnd: string, data: any = {}, method: string = "POST"): Promise<any> => {
        return this.send(`${Network.ODATA_BASE_URL}${urlEnd}`, data, method);
    };

    public send = async (url: string, data: any = {}, method: string = "POST"): Promise<any> => {
        const headers = await this.getHeaders();
        if (method === "PATCH") {
            headers.append("Prefer", "return=representation");
        }
        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: JSON.stringify(data),
        });
        Network.ensureOk(response, url);
        const body = (await response.text()).trim();
        if (!body) {
            // A 204 or an empty 200: the call succeeded but tells us nothing. Callers that need a payload
            // check for it themselves - same contract as before, when an unparsable body returned the response.
            return response;
        }
        try {
            return JSON.parse(body);
        } catch (e) {
            // request has no json available
            return response;
        }
    };

    /**
     * Posts a multipart body: one form field per value plus the boundary the browser sets.
     *
     * send() cannot carry one - it stringifies the body and declares it as JSON - which is why the
     * callers that upload files used to call fetch themselves, and with it stood outside the status check
     * every other request passes through. The response is handed back unread: an attachment
     * upload has no payload to read, while a started process answers with one.
     */
    public sendForm = async (url: string, form: FormData): Promise<Response> => {
        const headers = await this.getHeaders();
        // The browser sets multipart/form-data including the boundary, which it only does as long as we
        // declare no content-type of our own.
        headers.delete("content-type");
        const response = await fetch(url, {
            method: "POST",
            headers: headers,
            body: form,
        });
        Network.ensureOk(response, url);
        return response;
    };

    /**
     * Turns an error response into an ApiError before anyone tries to read its body.
     *
     * A rejected request may answer with an empty body, and parsing that surfaces as
     * "Unexpected end of JSON input" - a parser message that says nothing about the actual problem.
     */
    private static ensureOk = (response: Response, url: string): void => {
        if (response.ok) {
            return;
        }
        throw new ApiError(url, response.status, `request to ${Network.routeOf(url)} failed with status ${response.status}`);
    };

    /**
     * The url as the messages of an ApiError name it: without the query and without the key predicates.
     * Both carry values - the e-mail address a sender is looked up by, the mailbox a note is linked to in
     * CreateSyncSourceReference(…,Account='…') - and an error message reaches the log relay, where personal
     * data has no business and where a message unique to every request would also slip past the
     * suppression window. The full url stays on ApiError.url for the code that needs it.
     */
    private static routeOf = (url: string): string => {
        const queryStart = url.indexOf("?");
        return (queryStart < 0 ? url : url.substring(0, queryStart)).replace(/\([^)]*\)/g, "(…)");
    };

    /** Reads a successful response as JSON, reporting an empty or malformed body as such instead of as a parser error. */
    private static readJsonBody = async (response: Response, url: string): Promise<any> => {
        const body = (await response.text()).trim();
        if (!body) {
            throw new ApiError(url, response.status, `request to ${Network.routeOf(url)} answered status ${response.status} with an empty body`);
        }
        try {
            return JSON.parse(body);
        } catch (e) {
            throw new ApiError(url, response.status, `request to ${Network.routeOf(url)} answered status ${response.status} with a body that is not JSON`);
        }
    };

    protected getHeaders = async (): Promise<Headers> => {
        const token = await this.authenticate();
        const headers = new Headers();
        headers.append("Authorization", this.getAuthorizationHeader(token));
        headers.append("content-type", "application/json");
        headers.append("pragma", "no-cache");
        headers.append("cache-control", "no-cache");
        return headers;
    };

    public getAuthorizationHeader = (token: { token_type: string; access_token: string }): string => {
        if (!token) {
            return "";
        }
        return `${token.token_type} ${token.access_token}`;
    };

    public static get BaseURL(): string {
        return Network.BASE_URL;
    }

    public static get APIBaseURL(): string {
        return Network.API_BASE_URL;
    }

    public static get OdataBaseURL(): string {
        return Network.ODATA_BASE_URL;
    }

    public static get AddressParserURL(): string {
        return Network.ADDRESS_PARSER_URL;
    }
}
